import { supabase, isSupabaseConfigured, getCurrentUserId } from "@/lib/supabase";
import type { SuperPrompt, InsertSuperPrompt } from "@shared/schema";

class SuperPromptsService {
  async getSuperPrompts(): Promise<SuperPrompt[]> {
    if (!isSupabaseConfigured) return [];
    const userId = await getCurrentUserId();
    if (!userId) return [];

    const { data, error } = await supabase
      .from("super_prompts")
      .select("id, user_id, name, description, prompt_template, category, icon, color, variables, output_format, tags, is_favorite, usage_count, created_at, updated_at")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching super prompts:", error);
      return [];
    }

    return (data || []).map(this.mapToSuperPrompt);
  }

  async getSuperPromptById(id: string): Promise<SuperPrompt | null> {
    const { data, error } = await supabase
      .from("super_prompts")
      .select("id, user_id, name, description, prompt_template, category, icon, color, variables, output_format, tags, is_favorite, usage_count, created_at, updated_at")
      .eq("id", id)
      .single();

    if (error) {
      console.error("Error fetching super prompt:", error);
      return null;
    }

    return data ? this.mapToSuperPrompt(data) : null;
  }

  async createSuperPrompt(prompt: Omit<InsertSuperPrompt, "userId">): Promise<SuperPrompt | null> {
    if (!isSupabaseConfigured) return null;
    const userId = await getCurrentUserId();
    if (!userId) return null;

    const { data, error } = await supabase
      .from("super_prompts")
      .insert({
        user_id: userId,
        name: prompt.name,
        description: prompt.description || null,
        prompt_template: prompt.promptTemplate,
        category: prompt.category || "General",
        icon: prompt.icon || "Sparkles",
        color: prompt.color || "bg-purple-500",
        variables: prompt.variables || [],
        output_format: prompt.outputFormat || "markdown",
        tags: prompt.tags || [],
        is_favorite: prompt.isFavorite || "false",
        usage_count: prompt.usageCount || "0",
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating super prompt:", error);
      return null;
    }

    return data ? this.mapToSuperPrompt(data) : null;
  }

  async updateSuperPrompt(id: string, updates: Partial<InsertSuperPrompt>): Promise<SuperPrompt | null> {
    const updatePayload: Record<string, any> = {
      updated_at: new Date().toISOString(),
    };

    if (updates.name !== undefined) updatePayload.name = updates.name;
    if (updates.description !== undefined) updatePayload.description = updates.description;
    if (updates.promptTemplate !== undefined) updatePayload.prompt_template = updates.promptTemplate;
    if (updates.category !== undefined) updatePayload.category = updates.category;
    if (updates.icon !== undefined) updatePayload.icon = updates.icon;
    if (updates.color !== undefined) updatePayload.color = updates.color;
    if (updates.variables !== undefined) updatePayload.variables = updates.variables;
    if (updates.outputFormat !== undefined) updatePayload.output_format = updates.outputFormat;
    if (updates.tags !== undefined) updatePayload.tags = updates.tags;
    if (updates.isFavorite !== undefined) updatePayload.is_favorite = updates.isFavorite;
    if (updates.usageCount !== undefined) updatePayload.usage_count = updates.usageCount;

    const { data, error } = await supabase
      .from("super_prompts")
      .update(updatePayload)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("Error updating super prompt:", error);
      return null;
    }

    return data ? this.mapToSuperPrompt(data) : null;
  }

  async deleteSuperPrompt(id: string): Promise<boolean> {
    const { error } = await supabase
      .from("super_prompts")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Error deleting super prompt:", error);
      return false;
    }

    return true;
  }

  async incrementUsageCount(id: string): Promise<void> {
    const prompt = await this.getSuperPromptById(id);
    if (prompt) {
      const newCount = (parseInt(prompt.usageCount || "0") + 1).toString();
      await this.updateSuperPrompt(id, { usageCount: newCount });
    }
  }

  async toggleFavorite(id: string): Promise<SuperPrompt | null> {
    const prompt = await this.getSuperPromptById(id);
    if (!prompt) return null;

    const newFavorite = prompt.isFavorite === "true" ? "false" : "true";
    return this.updateSuperPrompt(id, { isFavorite: newFavorite });
  }

  private mapToSuperPrompt(data: any): SuperPrompt {
    return {
      id: data.id,
      userId: data.user_id,
      name: data.name,
      description: data.description,
      promptTemplate: data.prompt_template,
      category: data.category,
      icon: data.icon,
      color: data.color,
      variables: data.variables,
      outputFormat: data.output_format,
      tags: data.tags,
      isFavorite: data.is_favorite,
      usageCount: data.usage_count,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }
}

export const superPromptsService = new SuperPromptsService();
